import { useCallback, useRef } from 'react'
import axios from '@fs/zion-axios'
import { v4 as uuidv4 } from 'uuid'
import { useUser } from '@fs/zion-user'
import useRequestState from '../../shared/hooks/useRequestState'
import groupBySession from '../helpers/groupBySession'
import { dataURItoBlob, thumbnailDataURLFromBase64 } from '../../shared/helpers/image_helper'
import { useDSCState } from '../../state/DSCStore'

const commonServiceBase = '/service/discovery/common'

export const useGetKeepsakes = () => {
  const url = `${commonServiceBase}/patron/keepsakes`
  const getKeepsakes = useCallback(
    (dusid) => {
      const finalUrl = !dusid ? url : `${url}/guests/${dusid}`
      return axios.get(finalUrl).then((response) => {
        return groupBySession(response.data?.keepsakes)
      })
    },
    [url]
  )

  return useRequestState(getKeepsakes)
}

export const useUploadKeepsakeImage = () => {
  const dscState = useDSCState()
  const user = useUser()
  const email = dscState?.nonstandardUser?.email ?? user?.email ?? user?.loggedInUserContact?.email // We need to make sure this isn't going to get pulled out from under us.
  let url = user?.signedIn
    ? '/service/discovery/common/patron/keepsakes/memories/image'
    : '/service/discovery/common/patron/keepsakes/image'
  url = email ? `${url}?emailAddress=${encodeURIComponent(email)}` : url // Guest kiosk users may not have an email address

  const uploadKeepsakeImage = useCallback(
    async ({ imgDataURL, type = 'experienceImage' }) => {
      const targetBlob = dataURItoBlob(imgDataURL)
      const uuid = uuidv4()
      const fd = new FormData()
      fd.append('type', type)
      fd.append('imageFile', targetBlob, `${uuid}.jpg`)

      if (!user?.signedIn) {
        const thumbDataURL = await thumbnailDataURLFromBase64({ imgDataURL, maxWidth: 320, maxHeight: 320 })
        fd.append('storageType', 'patron')
        const thumbBlob = dataURItoBlob(thumbDataURL)
        fd.append('thumbnailFile', thumbBlob, `thumb_${uuid}.jpg`)
      }

      return axios.post(url, fd, null)
    },
    [url, user.signedIn]
  )

  const [upload, uploadState] = useRequestState(uploadKeepsakeImage)

  // Every upload stores another keepsake (S3 or Memories plus a keepsake row), so a repeat tap on the
  // same image while it is in flight must not send another (DSC-10353). Keyed by payload so a different
  // image (another recipe or relative) still uploads; set synchronously to catch same-tick taps.
  const inFlightRef = useRef(new Map())
  const guardedUpload = useCallback(
    (params) => {
      const key = `${params?.type ?? 'experienceImage'}:${params?.imgDataURL}`
      const inFlight = inFlightRef.current
      if (inFlight.has(key)) return inFlight.get(key)
      const request = upload(params).finally(() => {
        inFlight.delete(key)
      })
      inFlight.set(key, request)
      return request
    },
    [upload]
  )

  return [guardedUpload, uploadState]
}
